import React from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import DocumentAbstractHit from '@Components/Results/documents/DocumentAbstractHit';
import DocumentAbstract from '@Components/Results/documents/DocumentAbstract';
import DocumentClaims from '@Components/Results/documents/DocumentClaims';
import DocumentClaimsHit from '@Components/Results/documents/DocumentClaimsHit';
import DocumentBibliographicInfo from '@Components/Results/documents/DocumentBibliographicInfo';
import DocumentIndexTermsHit from '@Components/Results/documents/DocumentIndexTermsHit';
import DocumentIndexTerms from '@Components/Results/documents/DocumentIndexTerms';
import DocumentSubstances from '@Components/Results/documents/DocumentSubstances';
import DocumentReactions from '@Components/Results/documents/DocumentReactions';
import DocumentTargets from '@Components/Results/documents/DocumentTargets';

function DocumentCollapsibleContent({
  metadata,
  document: citation,
  zoom,
  toggleSection,
  abstractExpanded,
  claimsExpanded,
  indextermsExpanded,
  bibliographicInfoExpanded,
  substancesExpanded,
  hitSubstancesExpanded,
  reactionsExpanded,
  hitReactionsExpanded,
  targetsExpanded,
  getTrackingAttrs,
}) {
  const documentIndex = citation.index;
  const documentRelativeIndex = citation.relativeIndex;

  return (
    <>
      {/* Abstract */}
      {!!citation.abstractText && abstractExpanded && (
        <DocumentAbstract abstractText={citation.abstractText} closeClick={(e) => toggleSection(e, 'abstractExpanded')} />
      )}

      {/* Hit Abstract */}
      {!!citation.abstractText && !abstractExpanded && (
        <DocumentAbstractHit
          abstractText={citation.abstractText}
          closeClick={(e) => toggleSection(e, 'abstractExpanded')}
          trackingAttrs={getTrackingAttrs({
            'data-tracking': !abstractExpanded,
            'data-tracking-type': 'contentview',
            'data-tracking-action': 'abstract hit',
          })}
        />
      )}

      {/* Claims */}
      {!!citation.patentClaims && claimsExpanded && (
        <DocumentClaims claimsText={citation.patentClaims} closeClick={(e) => toggleSection(e, 'claimsExpanded')} />
      )}

      {/* Hit Claims */}
      {!!citation.patentClaims && !claimsExpanded && (
        <DocumentClaimsHit
          claimsText={citation.patentClaims}
          closeClick={(e) => toggleSection(e, 'claimsExpanded')}
          trackingAttrs={getTrackingAttrs({
            'data-tracking': !claimsExpanded,
            'data-tracking-type': 'contentview',
            'data-tracking-action': 'claims hit',
          })}
        />
      )}

      {/* Index Terms */}
      {!!citation.keywords && indextermsExpanded && (
        <DocumentIndexTerms keywords={citation.keywords} closeClick={(e) => toggleSection(e, 'indextermsExpanded')} />
      )}

      {/* Hit Index Terms */}
      {!!citation.keywords && !indextermsExpanded && (
        <DocumentIndexTermsHit
          keywords={citation.keywords}
          closeClick={(e) => toggleSection(e, 'indextermsExpanded')}
          trackingAttrs={getTrackingAttrs({
            'data-tracking': !indextermsExpanded,
            'data-tracking-type': 'contentview',
            'data-tracking-action': 'index terms hit',
          })}
        />
      )}

      {/* Substances */}
      {!!citation.substancesCount && substancesExpanded && (
        <DocumentSubstances
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          substancesCount={citation.substancesCount}
          isHit={false}
          zoom={zoom}
          closeClick={(e) => toggleSection(e, 'substancesExpanded')}
        />
      )}

      {/* Hit Substances */}
      {!!citation.substancesHitCount && hitSubstancesExpanded && (
        <DocumentSubstances
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          substancesCount={citation.substancesHitCount}
          isHit
          zoom={zoom}
          closeClick={(e) => toggleSection(e, 'hitSubstancesExpanded')}
        />
      )}

      {/* Reactions */}
      {!!citation.reactionsCount && reactionsExpanded && (
        <DocumentReactions
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          reactionsCount={citation.reactionsCount}
          isHit={false}
          zoom={zoom}
          closeClick={(e) => toggleSection(e, 'reactionsExpanded')}
        />
      )}

      {/* Hit Reactions */}
      {!!citation.reactionsHitCount && hitReactionsExpanded && (
        <DocumentReactions
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          reactionsCount={citation.reactionsHitCount}
          isHit
          zoom={zoom}
          closeClick={(e) => toggleSection(e, 'hitReactionsExpanded')}
        />
      )}

      {/* Targets */}
      {!!citation.targetsCount && targetsExpanded && (
        <DocumentTargets
          metadata={metadata}
          documentIndex={documentIndex}
          documentRelativeIndex={documentRelativeIndex}
          databaseId={citation.databaseId}
          targetsCount={citation.targetsCount}
          closeClick={(e) => toggleSection(e, 'targetsExpanded')}
        />
      )}

      {/* Bibliographic Info */}
      {bibliographicInfoExpanded && (
        <DocumentBibliographicInfo document={citation} closeClick={(e) => toggleSection(e, 'bibliographicInfoExpanded')} />
      )}
    </>
  );
}

DocumentCollapsibleContent.propTypes = {
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  toggleSection: PropTypes.func.isRequired,
  abstractExpanded: PropTypes.bool.isRequired,
  claimsExpanded: PropTypes.bool.isRequired,
  indextermsExpanded: PropTypes.bool.isRequired,
  bibliographicInfoExpanded: PropTypes.bool.isRequired,
  substancesExpanded: PropTypes.bool.isRequired,
  hitSubstancesExpanded: PropTypes.bool.isRequired,
  reactionsExpanded: PropTypes.bool.isRequired,
  hitReactionsExpanded: PropTypes.bool.isRequired,
  targetsExpanded: PropTypes.bool.isRequired,
  // for analytics
  getTrackingAttrs: PropTypes.func.isRequired,
};

export default DocumentCollapsibleContent;
